import axios from "axios";

// Double-click guard for every write request. While a POST/PUT/PATCH/DELETE is waiting for its
// response, an identical one (same method, URL and body) is never sent: the caller's promise
// simply stays pending, so no second toast or follow-up code runs. After a success the key stays
// locked for COOLDOWN_MS, because the screen needs a moment to update after the response and a
// fast double click often lands in that gap. After an error it is released at once so the user
// can retry. LOCK_MS is a safety expiry in case the response never arrives.
const WRITE_METHODS = new Set(["post", "put", "patch", "delete"]);
const LOCK_MS = 60_000;
const COOLDOWN_MS = 1000;
const inFlight = new Map();

const bodyKey = (data) => {
  if (typeof FormData !== "undefined" && data instanceof FormData) {
    return Array.from(data.entries()).map(([k, v]) => (
      v instanceof File ? `${k}:${v.name}:${v.size}` : `${k}:${v}`)).join("&");
  }
  return typeof data === "string" ? data : JSON.stringify(data ?? null);
};

const release = (config) => {
  if (config?.dedupeKey) inFlight.delete(config.dedupeKey);
};

axios.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (!WRITE_METHODS.has(method)) return config;
  const key = `${method} ${axios.getUri(config)} ${bodyKey(config.data)}`;
  const startedAt = inFlight.get(key);
  if (startedAt && Date.now() - startedAt < LOCK_MS) return new Promise(() => {});
  inFlight.set(key, Date.now());
  config.dedupeKey = key;
  return config;
});

axios.interceptors.response.use(
  (response) => {
    const key = response.config?.dedupeKey;
    if (key) inFlight.set(key, Date.now() - LOCK_MS + COOLDOWN_MS);
    return response;
  },
  (error) => { release(error.config); return Promise.reject(error); },
);
